/**
 * useCapacityStatus
 *
 * Lightweight polling hook that surfaces the master backend's
 * capacity guard state to UI components (status badge, banners,
 * etc.). Polls ``/api/system/capacity/`` every 30s while the tab
 * is visible; pauses when hidden to save bandwidth.
 *
 * The guard endpoint is cheap (per-process cache, ~5 ms cold,
 * near-zero warm) so a 30s poll is well within budget.
 *
 * Returns a status discriminator the consumer can render:
 *   - 'unknown': we haven't polled yet, or the endpoint isn't
 *     reachable. Render nothing.
 *   - 'ok':      all signals below WARN. Render nothing.
 *   - 'warn':    at least one signal between WARN and HARD.
 *     Render an amber pill — operators get a heads-up but
 *     work isn't being shed.
 *   - 'busy':    at least one signal past HARD; the guard is
 *     ACTIVELY refusing new work for ``cooldownActiveSeconds``.
 *     Render a red pill.
 *
 * The hook is GUARD-disabled-aware: when the backend reports
 * ``enabled=false`` (the safe default — see Phase 1 rollout in
 * the capacity guard MR) we never raise above 'ok' even if a
 * signal is past a threshold, because no work is actually being
 * shed. The badge would be a lie otherwise.
 */

import { useEffect, useRef, useState } from 'react'
import { quotechJson } from '../services/quotechApi'

export type CapacityStatus = 'unknown' | 'ok' | 'warn' | 'busy'

export interface CapacitySnapshot {
  enabled: boolean
  signals: {
    load_1min: number | null
    cpu_count: number
    load_per_cpu: number | null
    queue_depth_total: number | null
    queue_depth_per_queue: Record<string, number> | null
    redis_memory_pct: number | null
  }
  thresholds: {
    load_per_cpu_warn: number | null
    load_per_cpu_hard: number | null
    queue_depth_warn: number | null
    queue_depth_hard: number | null
    redis_mem_pct_warn: number | null
    redis_mem_pct_hard: number | null
    cooldown_seconds: number
  }
  cooldowns_active: Record<string, number>
}

export interface UseCapacityStatusResult {
  status: CapacityStatus
  trippedSignals: string[]
  cooldownActiveSeconds: number
  snapshot: CapacitySnapshot | null
  refresh: () => void
}

/** One tripped signal with the LIVE value behind it — so the operator
 *  can read *what* was busy (e.g. "Queue 1,820 / 1,500"), not just the
 *  name. */
export interface TrippedSignalDetail {
  name: string
  /** Short label: 'CPU' | 'Queue' | 'Memory'. */
  label: string
  /** "value / limit", e.g. '2.1× / 1.5×', '1,820 / 1,500', '86% / 80%'. */
  valueText: string
  level: 'warn' | 'hard'
}

const POLL_INTERVAL_MS = 30_000

/** Fire to force an immediate capacity re-read (the sync 429 handler
 *  dispatches this so the badge updates the instant the user is shed,
 *  instead of waiting for the next 30s tick). */
export const EMAIL_CAPACITY_REFRESH_EVENT = 'email-capacity-refresh'

const SIGNAL_LABELS: Record<string, string> = {
  load_per_cpu: 'CPU',
  queue_depth: 'Queue',
  redis_memory_pct: 'Memory',
}

function fmtCount(n: number): string {
  return n.toLocaleString()
}

/**
 * One-decimal format that counters IEEE-754 representation error.
 *
 * `(2.05).toFixed(1)` returns "2.0" because 2.05 is actually stored as
 * 2.0499999999999998 — so a CPU load of 2.05 rendered as "2.0×", quietly
 * under-reporting the busy signal. Nudging by a sub-precision epsilon
 * BEFORE the half-up round restores the human-expected "2.1".
 */
function fmtTimes(n: number): string {
  return (Math.round(n * 10 + 1e-9) / 10).toFixed(1)
}

/**
 * Describe every tripped signal with its live value vs the threshold
 * it breached. Pure (unit-tested without the network). Returns hard
 * signals first so the badge tooltip leads with the blocking ones.
 *
 * Unlike ``deriveStatus`` (which only needs names for the status pill),
 * this carries the NUMBERS so the UI can answer "what was busy?".
 */
export function describeTripped(
  snapshot: CapacitySnapshot | null,
): TrippedSignalDetail[] {
  if (!snapshot || !snapshot.enabled) return []
  const s = snapshot.signals
  const t = snapshot.thresholds
  const out: TrippedSignalDetail[] = []

  if (s.load_per_cpu !== null) {
    const v = s.load_per_cpu
    if (t.load_per_cpu_hard !== null && v >= t.load_per_cpu_hard) {
      out.push({ name: 'load_per_cpu', label: SIGNAL_LABELS.load_per_cpu, level: 'hard', valueText: `${fmtTimes(v)}× / ${fmtTimes(t.load_per_cpu_hard)}×` })
    } else if (t.load_per_cpu_warn !== null && v >= t.load_per_cpu_warn) {
      out.push({ name: 'load_per_cpu', label: SIGNAL_LABELS.load_per_cpu, level: 'warn', valueText: `${fmtTimes(v)}× / ${fmtTimes(t.load_per_cpu_warn)}×` })
    }
  }

  if (s.queue_depth_total !== null) {
    const v = s.queue_depth_total
    if (t.queue_depth_hard !== null && v >= t.queue_depth_hard) {
      out.push({ name: 'queue_depth', label: SIGNAL_LABELS.queue_depth, level: 'hard', valueText: `${fmtCount(v)} / ${fmtCount(t.queue_depth_hard)}` })
    } else if (t.queue_depth_warn !== null && v >= t.queue_depth_warn) {
      out.push({ name: 'queue_depth', label: SIGNAL_LABELS.queue_depth, level: 'warn', valueText: `${fmtCount(v)} / ${fmtCount(t.queue_depth_warn)}` })
    }
  }

  if (s.redis_memory_pct !== null) {
    const v = s.redis_memory_pct
    if (t.redis_mem_pct_hard !== null && v >= t.redis_mem_pct_hard) {
      out.push({ name: 'redis_memory_pct', label: SIGNAL_LABELS.redis_memory_pct, level: 'hard', valueText: `${Math.round(v)}% / ${Math.round(t.redis_mem_pct_hard)}%` })
    } else if (t.redis_mem_pct_warn !== null && v >= t.redis_mem_pct_warn) {
      out.push({ name: 'redis_memory_pct', label: SIGNAL_LABELS.redis_memory_pct, level: 'warn', valueText: `${Math.round(v)}% / ${Math.round(t.redis_mem_pct_warn)}%` })
    }
  }

  out.sort((a, b) => (a.level === b.level ? 0 : a.level === 'hard' ? -1 : 1))
  return out
}

/**
 * Decide the visible status from a snapshot.
 *
 * Pure function so it's covered by a single unit test rather
 * than via the network-binding hook.
 */
export function deriveStatus(
  snapshot: CapacitySnapshot | null,
): { status: CapacityStatus; trippedSignals: string[]; cooldownActiveSeconds: number } {
  if (!snapshot) return { status: 'unknown', trippedSignals: [], cooldownActiveSeconds: 0 }

  const cooldownEntries = Object.values(snapshot.cooldowns_active || {})
  const cooldownActiveSeconds = cooldownEntries.length > 0
    ? Math.ceil(Math.max(...cooldownEntries))
    : 0

  // Master switch off → never raise above 'ok' even if signals
  // are over thresholds. The guard isn't actually shedding work,
  // so the badge would lie.
  if (!snapshot.enabled) {
    return { status: 'ok', trippedSignals: [], cooldownActiveSeconds }
  }

  const tripped: { name: string; level: 'warn' | 'hard' }[] = []
  const s = snapshot.signals
  const t = snapshot.thresholds

  // Load per CPU
  if (s.load_per_cpu !== null) {
    if (t.load_per_cpu_hard !== null && s.load_per_cpu >= t.load_per_cpu_hard) {
      tripped.push({ name: 'load_per_cpu', level: 'hard' })
    } else if (t.load_per_cpu_warn !== null && s.load_per_cpu >= t.load_per_cpu_warn) {
      tripped.push({ name: 'load_per_cpu', level: 'warn' })
    }
  }

  // Queue depth
  if (s.queue_depth_total !== null) {
    if (t.queue_depth_hard !== null && s.queue_depth_total >= t.queue_depth_hard) {
      tripped.push({ name: 'queue_depth', level: 'hard' })
    } else if (t.queue_depth_warn !== null && s.queue_depth_total >= t.queue_depth_warn) {
      tripped.push({ name: 'queue_depth', level: 'warn' })
    }
  }

  // Redis memory %
  if (s.redis_memory_pct !== null) {
    if (t.redis_mem_pct_hard !== null && s.redis_memory_pct >= t.redis_mem_pct_hard) {
      tripped.push({ name: 'redis_memory_pct', level: 'hard' })
    } else if (t.redis_mem_pct_warn !== null && s.redis_memory_pct >= t.redis_mem_pct_warn) {
      tripped.push({ name: 'redis_memory_pct', level: 'warn' })
    }
  }

  // Active cool-downs always count as 'busy' even if signals
  // have improved — the backend is actively refusing work.
  if (cooldownActiveSeconds > 0) {
    return {
      status: 'busy',
      trippedSignals: tripped.map(x => x.name),
      cooldownActiveSeconds,
    }
  }

  if (tripped.some(x => x.level === 'hard')) {
    return {
      status: 'busy',
      trippedSignals: tripped.filter(x => x.level === 'hard').map(x => x.name),
      cooldownActiveSeconds,
    }
  }
  if (tripped.some(x => x.level === 'warn')) {
    return {
      status: 'warn',
      trippedSignals: tripped.filter(x => x.level === 'warn').map(x => x.name),
      cooldownActiveSeconds,
    }
  }
  return { status: 'ok', trippedSignals: [], cooldownActiveSeconds }
}

export function useCapacityStatus(): UseCapacityStatusResult {
  const [snapshot, setSnapshot] = useState<CapacitySnapshot | null>(null)
  const refreshRef = useRef<() => void>(() => {})

  useEffect(() => {
    let cancelled = false

    const poll = async () => {
      // Skip when the tab is hidden — there's no UI to update,
      // and the poll burns bandwidth + a Django request slot.
      if (typeof document !== 'undefined' && document.hidden) return
      try {
        const { data } = await quotechJson<CapacitySnapshot>(
          '/api/system/capacity/',
        )
        if (!cancelled) setSnapshot(data)
      } catch {
        // Endpoint not reachable / not deployed yet / 401 etc.
        // Treat as 'unknown' — the badge is hidden and we'll
        // try again next tick. Fail-open by design.
        if (!cancelled) setSnapshot(null)
      }
    }

    refreshRef.current = poll
    poll()
    const intervalId = window.setInterval(poll, POLL_INTERVAL_MS)
    const onVisibilityChange = () => { if (!document.hidden) poll() }
    document.addEventListener('visibilitychange', onVisibilityChange)
    // Immediate re-read on demand — the sync 429 handler fires this so
    // the badge reflects the shed right away instead of up to 30s later.
    const onRefresh = () => { void poll() }
    window.addEventListener(EMAIL_CAPACITY_REFRESH_EVENT, onRefresh as EventListener)

    return () => {
      cancelled = true
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener(EMAIL_CAPACITY_REFRESH_EVENT, onRefresh as EventListener)
    }
  }, [])

  const derived = deriveStatus(snapshot)

  return {
    status: derived.status,
    trippedSignals: derived.trippedSignals,
    cooldownActiveSeconds: derived.cooldownActiveSeconds,
    snapshot,
    refresh: () => refreshRef.current(),
  }
}
